import { motion } from "framer-motion";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";

const PolitiqueConfidentialitePage = () => {
  return (
    <SiteLayout>
      <SEOHead
        title="Politique de Confidentialité"
        description="Politique de confidentialité du site Autosur Montreuil-sous-Bois. Protection de vos données personnelles et vos droits RGPD."
        path="/politique-confidentialite"
        noindex
      />
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              INFORMATIONS LÉGALES
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Politique de confidentialité
            </h1>
            <p className="font-body text-white/70 text-sm">
              Dernière mise à jour : février 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            {/* Section 1 - Définitions */}
            <div className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-4">
                1. Définitions
              </h2>
              <div className="space-y-3 font-body text-muted-foreground">
                <p><strong>Client :</strong> Tout professionnel ou personne physique ou morale visitant le site.</p>
                <p><strong>Éditeur :</strong> B-TO CORP, société éditrice du site pour le compte d'Autosur Montreuil-sous-Bois.</p>
                <p><strong>Contenu :</strong> Textes, images, vidéos et tout élément présent sur le site.</p>
                <p><strong>Utilisateur :</strong> Internaute utilisant le site.</p>
                <p><strong>Informations personnelles :</strong> Données permettant d'identifier directement ou indirectement une personne physique (nom, prénom, email, téléphone, adresse IP, etc.).</p>
                <p><strong>RGPD :</strong> Règlement Général sur la Protection des Données (UE n°2016-679 du 27 avril 2016).</p>
                <p><strong>Responsable du traitement :</strong> La personne physique ou morale qui détermine les finalités et les moyens du traitement des données personnelles.</p>
                <p><strong>Cookies :</strong> Petits fichiers texte stockés sur le terminal de l'utilisateur lors de la navigation sur le site.</p>
                <p><strong>DPO (Délégué à la Protection des Données) :</strong> Personne chargée de veiller au respect de la réglementation en matière de protection des données.</p>
              </div>
            </div>

            {/* Introduction SECTA */}
            <div className="mb-10">
              <div className="bg-autosur-grey-light rounded-xl p-6 mb-6">
                <p className="font-heading font-bold text-autosur-navy text-lg mb-2">SECTA</p>
              </div>
              <p className="font-body text-muted-foreground leading-relaxed">
                SECTA et les Centres de contrôle (ci-après les "Centres") affiliés à ses Réseaux de contrôle technique (ci-après le "Réseau"), en leur qualité de responsable du traitement, attachent une grande importance à la protection et au respect de votre vie privée. La présente politique vise à vous informer, conformément au Règlement n°2016-679 du 27 avril 2016 (ci-après dénommé le « Règlement »), de nos pratiques concernant la collecte, l'utilisation et le partage des informations que vous êtes amenés à nous fournir par le biais de notre site internet (ci-après le «Site»), des Sites des Centres ainsi que leur agenda et lors des commandes de prestations de contrôle technique.
              </p>
              <p className="font-body text-muted-foreground leading-relaxed mt-4">
                Cette politique a pour but de vous informer sur les catégories de données personnelles que nous pourrions collecter ou détenir sur vous, comment nous les utilisons, avec qui nous les partageons, comment nous les protégeons, et les droits dont vous disposez sur vos données personnelles.
              </p>
            </div>

            {/* Section I */}
            <div className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-4">
                2. Les données que nous collectons
              </h2>
              <p className="font-body text-muted-foreground leading-relaxed mb-4">
                En utilisant notre Site et dans le cadre des services que les Centres sont amenés à réaliser pour votre compte, vous êtes amenés à nous transmettre des informations, dont certaines sont de nature à vous identifier et constituent de ce fait des données à caractère personnel (ci-après dénommées les «Données»). Ces Données contiennent notamment des données :
              </p>
              <ul className="space-y-3 font-body text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-autosur-red font-bold">•</span>
                  <span><strong>d'identification</strong> (le prénom, le numéro de téléphone, l'e-mail, l'adresse et le code postal, données de localisation, l'immatriculation et la marque/modèle du véhicule).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-autosur-red font-bold">•</span>
                  <span><strong>de transaction</strong> (les informations inscrites sur vos cartes de paiement)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-autosur-red font-bold">•</span>
                  <span><strong>techniques</strong> (cookies - Cf: <Link to="/politique-cookies" className="text-autosur-blue hover:text-autosur-navy underline">politique cookies</Link>)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-autosur-red font-bold">•</span>
                  <span><strong>relatives à votre navigation</strong> sur notre Site (par exemple: l'adresse IP, le site web référent, les pages visitées par votre appareil et le moment où votre appareil a visité notre Site, l'historique internet, collectés par le biais de tiers, tels que Google Analytics, qui ne vous identifient pas directement ou anonymisent les données).</span>
                </li>
              </ul>
            </div>

            {/* Section II */}
            <div className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-4">
                3. Comment utilisons-nous les données que nous collectons et sur quelle base légale ?
              </h2>
              <p className="font-body text-muted-foreground leading-relaxed mb-4">
                Nous utilisons les Données que nous collectons afin de :
              </p>
              <ul className="space-y-4 font-body text-muted-foreground">
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Vous rappeler la date de votre prochain contrôle technique</strong> au moyen de courriers (simples et/ou électroniques) et/ou de messages textes (SMS). <em className="text-autosur-navy">Ce traitement est fondé sur : notre intérêt légitime.</em>
                </li>
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Répondre à vos demandes d'information et de prise de contact</strong> formulées sur notre Site. <em className="text-autosur-navy">Ce traitement est fondé sur votre consentement.</em>
                </li>
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Exécuter ou finaliser toute prestation ou rendez-vous.</strong> <em className="text-autosur-navy">Ce traitement est fondé sur l'exécution d'un contrat.</em>
                </li>
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Gérer notre Site et effectuer des opérations techniques internes</strong> dans le cadre de résolution de problèmes, analyse de données, de tests, recherches, analyses, études et sondages. <em className="text-autosur-navy">Ce traitement est fondé sur notre intérêt légitime.</em>
                </li>
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Réaliser des enquêtes de satisfaction</strong> et publier des avis de façon anonyme. <em className="text-autosur-navy">Ce traitement est fondé sur notre intérêt légitime.</em>
                </li>
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Analyser votre provenance</strong> (téléphone, Internet…) afin d'effectuer des statistiques. <em className="text-autosur-navy">Ce traitement est fondé sur notre intérêt légitime.</em>
                </li>
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Analyser votre comportement sur le web ; Afficher les centres proches de votre localisation.</strong> <em className="text-autosur-navy">Ce traitement est fondé sur votre consentement sous la forme de l'acceptation via votre navigateur.</em>
                </li>
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Réaliser un calcul d'itinéraire vers le centre le plus proche</strong> via Google Maps avec les données que vous nous communiquez, le cas échéant, et pour le cas où ces données vous identifieraient personnellement. <em className="text-autosur-navy">Ce traitement est fondé sur l'exécution d'un contrat.</em>
                </li>
                <li className="pl-4 border-l-4 border-autosur-blue">
                  <strong>Effectuer des opérations de prospection, de communication, de fidélisation, de promotion, d'animations</strong> par l'envoi d'emails, courriers, appels ou de SMS. <em className="text-autosur-navy">Ce traitement est fondé sur : notre intérêt légitime de proposer de la sollicitation commerciale à un client, sauf opposition à la réception de ces offres de services et promotions ; votre consentement préalable en tant que prospect à la réception de la sollicitation commerciale.</em>
                </li>
              </ul>
              <p className="font-body text-muted-foreground leading-relaxed mt-4 italic">
                Lors de la collecte des Données, vous serez informé si certaines Données doivent être obligatoirement renseignées ou si elles sont facultatives. A défaut, l'exécution de votre demande pourra être restreinte ou impossible.
              </p>
            </div>

            {/* Section III */}
            <div className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-4">
                4. Qui sont les destinataires des données que nous collectons ?
              </h2>

              {/* III.1 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  1) Données transférées aux autorités et/ou organismes publics
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Conformément à la règlementation en vigueur, les Données peuvent être transmises aux autorités compétentes sur requête et notamment aux organismes publics, aux auxiliaires de justice, aux officiers ministériels, aux organismes chargés d'effectuer le recouvrement de créances, exclusivement pour répondre aux obligations légales, ainsi que dans le cas de la recherche des auteurs d'infractions commises sur internet.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Conformément à la règlementation applicable à notre activité, les Données sont transmises à l'Organisme Technique Central (OTC), organisme désigné par le ministre chargé des transports.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed">
                  A la demande des autorités dont nous dépendons et dans le cadre de campagnes de rappels constructeurs ayant pour objet la sauvegarde des intérêts vitaux des personnes concernées, les Données peuvent être communiquées à l'Organisme Technique Central (OTC), organisme désigné par le ministre chargé des transports. L'OTC communique ensuite les Données aux constructeurs et à leurs mandataires afin de leur permettre de contacter les automobilistes concernés.
                </p>
              </div>

              {/* III.2 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  2) Données transférées en interne
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  Les Données collectées et traitées sont transmises aux salariés de SECTA pour les questions ou domaines qui les concernent exclusivement, aux salariés des Centres ainsi qu'aux responsables légaux et/ou contacts des Centres.
                </p>
              </div>

              {/* III.3 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  3) Données transférées à des tiers
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Nous travaillons en étroite collaboration avec des prestataires de services et des sous-traitants lesquels peuvent avoir accès à vos Données.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Ces tiers réalisent pour notre compte certaines prestations à savoir :
                </p>
                <ul className="list-disc list-inside font-body text-muted-foreground space-y-1 ml-4 mb-4">
                  <li>Des services d'hébergement, de gestion, de stockage et de maintenance de toutes nos données,</li>
                  <li>Un service de prise de rendez-vous en ligne,</li>
                  <li>Un service de secrétariat téléphonique à distance pour la prise de rendez-vous,</li>
                  <li>Un service de relance par courrier pour vous rappeler la date de votre prochain contrôle technique,</li>
                  <li>Un service de paiement en ligne,</li>
                  <li>Des études, des sondages et des enquêtes de satisfaction,</li>
                  <li>Un service de calcul d'itinéraires,</li>
                  <li>La fourniture de programmes et plateformes d'animations.</li>
                </ul>
                <p className="font-body text-muted-foreground leading-relaxed">
                  Nous transmettons à ces tiers uniquement les Données dont ils ont besoin pour effectuer leurs prestations de services et nous exigeons qu'ils n'utilisent pas vos Données à d'autres fins. Ces tiers agissent uniquement et conformément à nos instructions et sont contractuellement tenus d'assurer un niveau de sécurité et de confidentialité de vos Données identique au nôtre. Nous leur demandons également de se conformer à la règlementation applicable à la protection des données à caractère personnel.
                </p>
              </div>

              {/* III.4 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  4) Combien de temps conservons-nous vos données?
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Vos Données ne seront pas conservées au-delà de la durée strictement nécessaire aux finalités poursuivies telles qu'énoncées dans cette Politique et ce conformément au Règlement et aux lois applicables.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  A cet égard, les Données utilisées aux fins d'exécution des prestations de contrôle technique que vous confiez aux Centres sont conservées pendant toute la durée de la relation contractuelle, puis archivées conformément aux délais de prescription.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Les Données utilisées aux fins de prospection, de communication, de fidélisation, de promotions, d'animations peuvent être conservées pendant une durée maximale de <strong>4 ans et cinq mois</strong> ou <strong>6 ans et cinq mois</strong> pour un véhicule de collection à compter de la fin de la relation commerciale ou du dernier contact du prospect concerné.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Vos Données sont effacées lorsque les durées de conservation expirent. Néanmoins, vos Données pourront être archivées au-delà des durées prévues pour les besoins de la recherche, la constatation et de la poursuite des infractions pénales dans le seul but de permettre, en tant que de besoin, la mise à disposition de vos Données à l'autorité judiciaire.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Les Données utilisées aux fins de calcul d'itinéraires sont conservées uniquement pendant votre session de navigation sur notre site.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed italic">
                  L'archivage implique que vos Données ne seront plus consultables en ligne mais seront extraites et conservées sur un support autonome et sécurisé.
                </p>
              </div>

              {/* III.5 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  5) Vos données sont-elles transférées, comment et où?
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Vos Données sont susceptibles d'être transférées d'un pays situé dans l'Union européenne vers un pays situé en dehors de l'Union Européenne.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Lorsque nous transférons vos Données en dehors de l'Union Européenne, nous le ferons toujours de manière sécurisée et légale :
                </p>
                <ul className="list-disc list-inside font-body text-muted-foreground space-y-2 ml-4 mb-3">
                  <li>Soit en transférant les Données à un destinataire situé dans un pays ayant fait l'objet d'une décision d'adéquation de la Commission européenne certifiant qu'il dispose d'un niveau de protection adéquat;</li>
                  <li>Soit par la signature préalable entre l'importateur et l'exportateur de Données, des Clauses Contractuelles Types qui ont été approuvées par la Commission européenne comme assurant un niveau de protection adéquat de vos Données;</li>
                  <li>Soit par le recours à des Règles Internes d'Entreprise Contraignantes validées par les autorités de protection des données compétentes;</li>
                  <li>Soit en recourant à toutes garanties appropriées visées à l'article 46 du Règlement.</li>
                </ul>
                <p className="font-body text-muted-foreground leading-relaxed">
                  Vous pouvez avoir accès à l'ensemble de ces garanties en nous contactant à l'adresse mentionnée à l'article 9 des présentes.
                </p>
              </div>

              {/* III.6 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  6) Comment vos données sont protégées?
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Nous prenons des mesures adéquates sur le plan technique et organisationnel pour garantir la protection de vos données contre tout accès non autorisé ou la modification, divulgation, perte ou destruction de vos Données.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mb-3">
                  Nous mettons en œuvre des mesures qui respectent les principes de protection dès la conception et par défaut des données.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed">
                  En cas de recours à un prestataire, nous nous assurons que ce dernier respecte ses obligations en matière de sécurité préalablement à la communication de vos données.
                </p>
              </div>

              {/* III.7 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  7) Quels sont vos droits sur vos données et comment les exercer?
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed mb-4">
                  Conformément aux lois et règlements applicables en matière de protection des données personnelles, vous bénéficiez d'un certain nombre de droits relatifs à vos Données, à savoir :
                </p>
                <div className="space-y-4">
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Un droit d'accès et d'information</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Vous avez le droit d'être informé de manière concise, transparente, intelligible et facilement accessible de la manière dont vos Données sont traitées. Vous avez également le droit d'obtenir la confirmation que des Données vous concernant sont traitées et, le cas échéant, d'accéder à ces Données et d'en obtenir une copie.
                    </p>
                  </div>
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Un droit de rectification</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Vous avez le droit d'obtenir la rectification des Données inexactes vous concernant. Vous avez également le droit de compléter les Données incomplètes vous concernant, en fournissant une déclaration complémentaire. En cas d'exercice de ce droit, nous nous engageons à communiquer toute rectification à l'ensemble des destinataires de vos Données.
                    </p>
                  </div>
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Un droit d'effacement</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Dans certains cas, vous avez le droit d'obtenir l'effacement de vos Données. Cependant, ceci n'est pas un droit absolu et nous pouvons pour des raisons légales ou légitimes conserver ces Données.
                    </p>
                  </div>
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Un droit à la limitation du traitement</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Dans certains cas, vous avez le droit d'obtenir la limitation du traitement sur vos Données.
                    </p>
                  </div>
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Un droit à la portabilité des Données</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Vous avez le droit de recevoir vos Données que vous nous avez fournies, dans un format structuré, couramment utilisé et lisible par une machine, pour votre usage personnel ou pour les transmettre à un tiers de votre choix. Ce droit ne s'applique que lorsque le traitement de vos Données est basé sur votre consentement, sur un contrat et que ce traitement est effectué à l'aide de procédés automatisés.
                    </p>
                  </div>
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Un droit d'opposition au traitement</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Vous avez le droit de vous opposer à tout moment au traitement de vos Données pour les traitements basés sur notre intérêt légitime, une mission d'intérêt public et ceux à des fins de prospection commerciale. Ceci n'est pas un droit absolu et nous pouvons pour des raisons légales ou légitimes refuser votre demande d'opposition.
                    </p>
                  </div>
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Le droit de retirer votre consentement à tout moment</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Vous pouvez retirer votre consentement au traitement de vos Données lorsque le traitement est basé sur votre consentement. Le retrait du consentement ne compromet pas la licéité du traitement fondé sur le consentement effectué avant ce retrait.
                    </p>
                  </div>
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Le droit de déposer une plainte auprès d'une autorité de contrôle</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Vous avez le droit de contacter votre autorité de protection des données pour vous plaindre de nos pratiques de protection des données personnelles.
                    </p>
                  </div>
                  <div className="bg-autosur-grey-light rounded-lg p-4">
                    <h4 className="font-heading font-semibold text-autosur-navy mb-2">Le droit de donner des directives concernant le sort de vos données après votre décès</h4>
                    <p className="font-body text-muted-foreground text-sm">
                      Vous avez le droit de nous donner des directives concernant l'utilisation de vos Données après votre décès.
                    </p>
                  </div>
                </div>
                <p className="font-body text-muted-foreground leading-relaxed mt-4">
                  Pour exercer ces droits, vous pouvez nous contacter à l'adresse suivante :{" "}
                  <a href="mailto:autosur.montreuil@gmail.com" className="text-autosur-blue hover:text-autosur-navy underline">autosur.montreuil@gmail.com</a>.
                </p>
                <p className="font-body text-muted-foreground leading-relaxed mt-2 italic">
                  Nous attirons votre attention sur le fait que nous pouvons exiger un justificatif de votre identité pour l'exercice des droits énumérés ci-dessus.
                </p>
              </div>

              {/* III.8 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  8) Modification et application de la politique de confidentialité
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  Cette politique de confidentialité est susceptible d'être modifiée à tout moment afin de se conformer à toutes évolutions réglementaires, jurisprudentielles, éditoriales ou techniques.
                </p>
              </div>

              {/* III.9 */}
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-autosur-navy mb-3">
                  9) Contact
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed mb-4">
                  Pour toute question relative à la présente politique ou pour toute demande relative à vos Données, vous pouvez nous contacter :
                </p>
                <div className="bg-autosur-grey-light rounded-xl p-6">
                  <p className="font-body text-muted-foreground mb-2">
                    <strong>Par courrier électronique :</strong>{" "}
                    <a href="mailto:autosur.montreuil@gmail.com" className="text-autosur-blue hover:text-autosur-navy underline">autosur.montreuil@gmail.com</a>
                  </p>
                  <p className="font-body text-muted-foreground">
                    <strong>Par courrier postal :</strong><br />
                    20 ter, rue de Bezons<br />
                    92415 Courbevoie Cedex
                  </p>
                </div>
              </div>
            </div>

            {/* Back link */}
            <div className="pt-8 border-t border-gray-200">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-autosur-blue hover:text-autosur-navy font-heading font-semibold transition-colors"
              >
                ← Retour à l'accueil
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default PolitiqueConfidentialitePage;
